import { FC } from "react";

import styles from "./CountdownRing.module.css";

type Props = {
  secondsRemaining: number;
  totalSeconds: number;
  colour: string;
};

const SIZE = 56;
const STROKE = 5;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * A ring that empties as the offer runs out.
 *
 * Linear, deliberately: easing a clock misrepresents how much time is left. The
 * sweep is a stroke-dashoffset transition rather than a keyframe animation so it
 * retargets smoothly when the countdown pauses and resumes.
 */
const CountdownRing: FC<Props> = ({
  secondsRemaining,
  totalSeconds,
  colour,
}) => {
  const progress = totalSeconds > 0 ? secondsRemaining / totalSeconds : 0;

  return (
    <div
      className={styles.wrapper}
      role="timer"
      // The number changes every second; announcing each tick would be hostile
      aria-live="off"
      aria-label={`${secondsRemaining} seconds remaining`}
    >
      <svg width={SIZE} height={SIZE} className={styles.svg} aria-hidden="true">
        <circle
          className={styles.track}
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          strokeWidth={STROKE}
          fill="none"
        />
        <circle
          className={styles.progress}
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          strokeWidth={STROKE}
          fill="none"
          stroke={colour}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE * (1 - progress)}
        />
      </svg>
      <span className={styles.value}>{secondsRemaining}</span>
    </div>
  );
};

export default CountdownRing;
